import { Link } from "react-router-dom";
import { 
  Database, Zap, Shield, ChevronRight, Code2, 
  ArrowRightLeft, LayoutTemplate, CheckCircle2, 
  TrendingUp, Clock, Users, BrainCircuit, LineChart, Target, Check, X
} from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white py-24 sm:py-32">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[1000px] h-[1000px] rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-tr from-blue-500/20 to-cyan-500/20 blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-sm font-medium mb-8">
            <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
            Uplatz Text2SQL
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">
            Natural Language ↔ SQL <br className="hidden md:block" /> Made Effortless
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-slate-300 mb-10 leading-relaxed">
            The ultimate Uplatz Text2SQL solution to instantly translate plain English into complex SQL queries, and decode complex SQL back into human-readable text. Empower your entire team with seamless database access without writing a single line of code.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/convert-text-2-sql" className="group relative inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg hover:shadow-indigo-500/25 w-full sm:w-auto">
              Try Text to SQL
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/convert-sql-2-text" className="group inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-white font-semibold px-8 py-4 rounded-xl transition-all w-full sm:w-auto">
              Explain SQL
              <Code2 className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How It Works 🚀</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Get from a simple question to a complex query in three easy steps.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 relative">
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-indigo-100 via-indigo-300 to-purple-100 z-0"></div>
            
            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-indigo-50 border-4 border-white shadow-xl flex items-center justify-center text-4xl mb-6">
                💬
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">1. Ask a Question</h3>
              <p className="text-slate-600">Type your data request in everyday English. For example: "Show me the top 5 customers by sales this month."</p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-purple-50 border-4 border-white shadow-xl flex items-center justify-center text-4xl mb-6">
                🧠
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">2. AI Translates</h3>
              <p className="text-slate-600">Our advanced AI understands your schema and context to instantly generate the precise, optimized SQL code.</p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-blue-50 border-4 border-white shadow-xl flex items-center justify-center text-4xl mb-6">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">3. Get Results</h3>
              <p className="text-slate-600">Execute the generated query directly or copy it into your database tool to get the insights you need immediately.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why Choose Uplatz Text2SQL?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">We combine cutting-edge AI with enterprise-grade reliability.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <BrainCircuit className="w-10 h-10 text-indigo-600 mb-5" />
              <h3 className="text-lg font-bold text-slate-900 mb-3">Context-Aware AI</h3>
              <p className="text-slate-600">Our models are trained on complex enterprise schemas to understand the nuances of your specific database.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <Shield className="w-10 h-10 text-green-600 mb-5" />
              <h3 className="text-lg font-bold text-slate-900 mb-3">Enterprise Security</h3>
              <p className="text-slate-600">Your schema and data never leave your control. We ensure strict data privacy and compliance standards.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <Zap className="w-10 h-10 text-amber-500 mb-5" />
              <h3 className="text-lg font-bold text-slate-900 mb-3">Lightning Fast</h3>
              <p className="text-slate-600">Get queries generated and explained in milliseconds, keeping your team's workflow uninterrupted.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <Target className="w-10 h-10 text-purple-600 mb-5" />
              <h3 className="text-lg font-bold text-slate-900 mb-3">High Accuracy</h3>
              <p className="text-slate-600">Consistently correct query generation that handles complex joins, subqueries, and window functions easily.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Outcome Section */}
      <section className="py-24 bg-indigo-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">The Outcome</h2>
              <p className="text-xl text-indigo-200 mb-8 leading-relaxed">
                Transforming how your organization interacts with data. Say goodbye to the analytics bottleneck and hello to instant insights.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 bg-indigo-500/30 p-2 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold">10x Faster Insights</h4>
                    <p className="text-indigo-200 mt-1">Eliminate the back-and-forth between business teams and data engineers.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 bg-indigo-500/30 p-2 rounded-lg">
                    <Users className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold">Empowered Teams</h4>
                    <p className="text-indigo-200 mt-1">Product managers, marketers, and sales can query databases independently.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1 bg-indigo-500/30 p-2 rounded-lg">
                    <Clock className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold">Saved Engineering Hours</h4>
                    <p className="text-indigo-200 mt-1">Free up your technical resources from writing repetitive ad-hoc queries.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 p-8 rounded-3xl backdrop-blur-md border border-white/20">
              <LineChart className="w-16 h-16 text-indigo-300 mb-6" />
              <div className="text-5xl font-extrabold text-white mb-2">85%</div>
              <p className="text-indigo-200 text-lg mb-8">Reduction in time-to-insight for non-technical users.</p>
              
              <div className="text-5xl font-extrabold text-white mb-2">40+</div>
              <p className="text-indigo-200 text-lg">Hours saved per month per data analyst.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section Overview */}
      {/* <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Start for free, upgrade when you need more power.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Basic</h3>
              <div className="text-4xl font-extrabold text-slate-900 mb-6">Free</div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">100 Conversions/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">Basic support</span>
                </li>
              </ul>
              <Link to="/register" className="block w-full py-3 px-6 rounded-xl font-semibold text-center bg-white border border-slate-300 hover:bg-slate-50 text-slate-900 transition-colors">
                Get Started
              </Link>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-indigo-500 shadow-xl shadow-indigo-100 relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white px-4 py-1 rounded-full text-sm font-semibold tracking-wide">
                POPULAR
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Pro</h3>
              <div className="text-4xl font-extrabold text-slate-900 mb-6">$29<span className="text-lg text-slate-500 font-normal">/mo</span></div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">Unlimited Conversions</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">Priority Support</span>
                </li>
              </ul>
              <Link to="/register" className="block w-full py-3 px-6 rounded-xl font-semibold text-center bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                Upgrade to Pro
              </Link>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/pricing" className="text-indigo-600 font-semibold hover:text-indigo-700 inline-flex items-center gap-2">
              View full pricing details <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section> */}

    </div>
  );
}
